import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { ArrowLeft, Loader2, Play } from "lucide-react";
import { Button } from "@/components/ui/base/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/base/table";
import { useStateMachine } from "@/core/arch/state_machine";
import { JobHistoryBloc } from "@/application/tools/job_history_bloc";
import { jobHistoryRepository } from "@/application/tools/job_history_repository";
import { ROUTES } from "@/routes";
import { TOOLS } from "./tools";
import { useHasGroup } from "@/hooks/useHasGroup";
import { useNowTicker } from "@/hooks/useNowTicker";
import { isTerminal } from "@/application/tools/job_polling";
import { JobStatusBadge } from "./components/JobStatusBadge";
import { DevelopersOnlyCard } from "./components/DevelopersOnlyCard";
import {
  chainLabel,
  formatAbsolute,
  formatDuration,
} from "./components/job_display";
import { JobDetailSheet } from "./components/JobDetailSheet";
import { RunOneClickToolSheet } from "./components/RunOneClickToolSheet";

export function JobHistoryView() {
  const { toolId = "" } = useParams<{ toolId: string }>();
  const isDeveloper = useHasGroup("CPOS-Developers");
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedJobId = searchParams.get("job");

  // The selected run lives in the URL so a failing run can be linked to
  // directly, and so back closes the sheet rather than leaving the page.
  const selectJob = useCallback(
    (id: string | null) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (id) next.set("job", id);
          else next.delete("job");
          return next;
        },
        { replace: !id },
      );
    },
    [setSearchParams],
  );

  const navigate = useNavigate();
  const [runOpen, setRunOpen] = useState(false);

  const tool = useMemo(
    () => TOOLS.find((t) => t.id === toolId) ?? null,
    [toolId],
  );
  const jobType = tool?.jobType ?? "";
  const title = tool?.title ?? toolId;

  const [state, bloc] = useStateMachine(
    () => new JobHistoryBloc(jobHistoryRepository, jobType),
  );

  useEffect(() => {
    if (!isDeveloper) return;
    void bloc.load(jobType);
  }, [bloc, jobType, isDeveloper]);

  // Ticks per second only while a run can still be advancing; a settled
  // history reads its duration off completedAt and needs no clock.
  const now = useNowTicker(state.rows.some((row) => !isTerminal(row.status)));

  if (!isDeveloper) return <DevelopersOnlyCard what="Job history" />;

  return (
    <div>
      <Link
        to={ROUTES.TOOLS}
        className="text-muted-foreground hover:text-foreground mb-4 inline-flex items-center gap-1 text-sm"
      >
        <ArrowLeft className="h-3 w-3" />
        Tools
      </Link>

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-foreground text-2xl font-bold">
            {title} history
          </h1>
          <p className="text-subtle-foreground mt-1 font-mono text-xs">
            {jobType}
          </p>
        </div>
        {tool && (
          <Button
            onClick={() =>
              tool.kind === "route" ? navigate(tool.path) : setRunOpen(true)
            }
          >
            <Play className="mr-1 h-3 w-3" />
            {tool.kind === "route" ? "Open" : "Run"}
          </Button>
        )}
      </div>

      {tool?.description && (
        <p className="text-muted-foreground mt-3 mb-6 max-w-3xl text-sm">
          {tool.description}
        </p>
      )}

      {state.status === "error" && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {state.error}
        </div>
      )}

      {state.status === "loading" && (
        <p className="text-muted-foreground text-sm">Loading run history…</p>
      )}

      {state.status === "loaded" && state.rows.length === 0 && (
        <div className="border-border bg-muted text-muted-foreground rounded-md border p-4 text-sm">
          No runs recorded for this tool yet.
        </div>
      )}

      {state.rows.length > 0 && (
        <>
          <div className="bg-card rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Enqueued</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Chain</TableHead>
                  <TableHead className="text-right">Processed</TableHead>
                  <TableHead className="text-right">Duration</TableHead>
                  <TableHead>Triggered by</TableHead>
                  <TableHead>Job ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {state.rows.map((row) => (
                  <TableRow
                    key={row.id}
                    onClick={() => selectJob(row.id)}
                    className="hover:bg-muted cursor-pointer"
                  >
                    <TableCell className="text-foreground align-top whitespace-nowrap">
                      {formatAbsolute(row.enqueuedAt)}
                    </TableCell>
                    <TableCell className="align-top">
                      <JobStatusBadge status={row.status} />
                    </TableCell>
                    <TableCell
                      className="text-muted-foreground align-top text-xs whitespace-nowrap"
                      title={row.chainRoot ?? undefined}
                    >
                      {chainLabel(row.link)}
                    </TableCell>
                    <TableCell className="text-foreground text-right align-top tabular-nums">
                      {row.processed}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-right align-top whitespace-nowrap">
                      {formatDuration(row.startedAt, row.completedAt, now) ??
                        "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground align-top text-xs">
                      {row.triggeredBy}
                    </TableCell>
                    <TableCell className="text-subtle-foreground align-top font-mono text-xs">
                      {row.id}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {state.nextToken && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                disabled={state.loadingMore}
                onClick={() => bloc.loadMore()}
              >
                {state.loadingMore && (
                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                )}
                Load more
              </Button>
            </div>
          )}
        </>
      )}

      {/* Reload on close: a run started here is absent from the loaded rows,
          and an all-terminal history has no poll running to discover it. */}
      {runOpen && tool?.kind === "custom" && (
        <tool.Sheet
          onClose={() => {
            setRunOpen(false);
            void bloc.load(jobType);
          }}
        />
      )}
      {runOpen && tool?.kind === "oneClick" && (
        <RunOneClickToolSheet
          title={tool.title}
          description={tool.description}
          run={tool.run}
          onClose={() => {
            setRunOpen(false);
            void bloc.load(jobType);
          }}
        />
      )}

      {/* Keyed on the id so switching rows remounts the bloc rather than
          showing the previous run until the new fetch lands. */}
      {selectedJobId && (
        <JobDetailSheet
          key={selectedJobId}
          jobId={selectedJobId}
          onClose={() => selectJob(null)}
        />
      )}
    </div>
  );
}
